import { useState, useCallback } from 'react';
import {
  Elements,
  PaymentElement,
  LinkAuthenticationElement,
  AddressElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button } from '@scio3/ui';
import { useTranslation } from 'react-i18next';
import { useStripeSetup } from '../hooks/useStripeSetup';
import { useStripeAppearance } from '../hooks/useStripeAppearance';

type StripeCardFormProps = {
  /** Stripe client secret from stripeBuildElement query */
  clientSecret: string;
  /** Payment method order from stripeBuildElement query */
  paymentMethodOrder?: string[];
  /** Default email for LinkAuthenticationElement */
  defaultEmail?: string;
  /** Slot rendered inline next to Email field (e.g. "Enter Promo code" link) */
  emailInlineSlot?: React.ReactNode;
  /** Hide the email (LinkAuthenticationElement) field — use when email is managed separately */
  hideEmail?: boolean;
  /** Submit button text */
  submitText?: string;
  /** Called on successful setup/payment confirmation. May be async — the form
   *  awaits this before re-enabling the submit button so async parent work
   *  (registerPaymentMethod mutation, refetch, confirm dialog) can't be
   *  interrupted by a duplicate Save click. (#1076) */
  onSuccess: (setupIntentId: string) => Promise<void> | void;
  /** Called when billing address changes in AddressElement — use to capture address for redirect flows */
  onBillingAddressChange?: (address: Record<string, string> | null) => void;
  /** Called on error */
  onError?: (message: string) => void;
  /** Called when form submit starts (before Stripe confirmSetup) — use to show loading in external button */
  onSubmitStart?: () => void;
  /** Return URL for 3DS redirect */
  returnUrl?: string;
  /** Show submit button (false if parent handles submit) */
  showSubmitButton?: boolean;
  /** Loading state from parent */
  isLoading?: boolean;
  /** Show billing address fields (AddressElement) — needed for non-card payment methods
   *  (iDEAL, SEPA, Bancontact) so Stripe has a valid location for automatic tax calculation */
  showBillingAddress?: boolean;
  /** Default billing address name (pre-fill AddressElement) */
  defaultBillingName?: string;
};

/**
 * Inner form component — must be inside <Elements> provider.
 */
function StripePaymentForm({
  submitText,
  onSuccess,
  onError,
  returnUrl,
  showSubmitButton = true,
  isLoading = false,
  defaultEmail,
  emailInlineSlot,
  hideEmail = false,
  paymentMethodOrder,
  onSubmitStart,
  showBillingAddress = false,
  defaultBillingName,
  onBillingAddressChange,
}: Omit<StripeCardFormProps, 'clientSecret'> & {
  defaultEmail?: string;
  emailInlineSlot?: React.ReactNode;
  hideEmail?: boolean;
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Track selected payment method type — AddressElement only shown for non-card methods
  // (matching scio1: card has its own Country field, non-card needs explicit billing address).
  const [paymentMethodType, setPaymentMethodType] = useState<string>('card');

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      if (!stripe || !elements || submitting) return;

      setSubmitting(true);
      setErrorMessage(null);
      onSubmitStart?.();

      // #1127 — wrap stripe.confirmSetup in try/catch so SDK-thrown errors
      // (e.g., IntegrationError when fields are opted-out but not provided) surface
      // as state instead of becoming an uncaught promise rejection that leaves
      // the button stuck on "Processing..." forever.
      let error: { message?: string; type?: string } | undefined;
      let setupIntent:
        | {
            status?: string;
            payment_method?: string | { id?: string } | null;
            id: string;
          }
        | undefined;
      try {
        const result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: returnUrl ?? `${window.location.origin}/app/s/payment-intent`,
          },
          redirect: 'if_required',
        });
        error = result.error;
        setupIntent = result.setupIntent;
      } catch (e) {
        const message = e instanceof Error ? e.message : t('Payment failed. Please try again.');
        setErrorMessage(message);
        onError?.(message);
        setSubmitting(false);
        return;
      }

      if (error) {
        const message = error.message ?? t('Payment failed. Please try again.');
        // #1314 — For Stripe validation errors (incomplete card / expiry / cvc),
        // PaymentElement already renders an inline error anchored to each
        // offending field. Surfacing the same message again as a local <p>
        // summary below the form created a confusing duplicate. Skip the
        // local render for validation_error; consumer's onError still fires
        // (typically a toast) for visibility.
        if (error.type !== 'validation_error') {
          setErrorMessage(message);
        }
        onError?.(message);
        setSubmitting(false);
        return;
      }

      // Handle both immediate success (card) and deferred processing (SEPA Debit).
      // SEPA Debit returns 'processing' — the payment method is set up asynchronously.
      if (setupIntent?.status === 'succeeded' || setupIntent?.status === 'processing') {
        // Pass the PaymentMethod ID (pm_...), not the SetupIntent ID (seti_...)
        const paymentMethodId =
          typeof setupIntent.payment_method === 'string'
            ? setupIntent.payment_method
            : (setupIntent.payment_method?.id ?? setupIntent.id);
        try {
          await onSuccess(paymentMethodId);
        } finally {
          setSubmitting(false);
        }
        return;
      }

      // For redirect-based methods (Bancontact/iDEAL): stripe.confirmSetup() normally
      // redirects the browser — this code is unreachable in the happy path. But if we
      // arrive here (Stripe rejected the combo silently, network issue, redirect blocked,
      // or unexpected status like requires_action / requires_confirmation /
      // requires_payment_method / canceled), do NOT leave the button stuck on
      // "Processing..." forever — surface the situation and let the user retry. (#1127)
      const message = setupIntent?.status
        ? t('Payment was not completed. Please try again or use a different payment method.')
        : t('Payment failed. Please try again.');
      setErrorMessage(message);
      onError?.(message);
      setSubmitting(false);
    },
    [stripe, elements, submitting, returnUrl, onSuccess, onError, onSubmitStart, t],
  );

  return (
    <form id="stripe-payment-form" onSubmit={handleSubmit} className="space-y-4">
      {/* Email + optional inline slot (e.g. Promo code) — matching scio1 two-column layout */}
      {!hideEmail && (
        <div className="grid grid-cols-[1fr_auto] gap-4 items-end">
          <LinkAuthenticationElement
            options={defaultEmail ? { defaultValues: { email: defaultEmail } } : undefined}
          />
          {emailInlineSlot && <div>{emailInlineSlot}</div>}
        </div>
      )}
      {/* Billing address — only shown for non-card methods (iDEAL, SEPA,
          Bancontact, US Bank Account) that need an explicit address for Stripe
          automatic tax. Card mode keeps a single PaymentElement with no extra
          AddressElement. */}
      {showBillingAddress && paymentMethodType !== 'card' && (
        <AddressElement
          onChange={(event) => {
            if (event.complete) {
              onBillingAddressChange?.(event.value.address as Record<string, string>);
            }
          }}
          options={{
            mode: 'billing',
            fields: { phone: 'never' },
            ...(defaultBillingName ? { defaultValues: { name: defaultBillingName } } : {}),
          }}
        />
      )}
      <PaymentElement
        onChange={(event) => setPaymentMethodType(event.value.type)}
        options={{
          layout: 'tabs',
          ...(paymentMethodOrder?.length ? { paymentMethodOrder } : {}),
          // Static `fields` config (#1156). Stripe Elements treats the
          // billingDetails opt-out as MOUNT-LOCKED — once `name: 'never'` is
          // applied, removing the prop later does NOT restore the name field.
          // The previous code toggled the name opt-out on `paymentMethodType !==
          // 'card'`, so switching to US Bank Account and back left Card stuck in
          // opt-out state and confirmSetup threw "You specified 'never' for
          // fields.billing_details.name...". Now we NEVER opt out name —
          // PaymentElement collects it on every tab (Card → "Name on card",
          // Bank → "Account holder name"). The AddressElement above for non-card
          // methods also has a name field, so non-card flows ask for name twice;
          // accepted as the simpler trade-off vs. the toggle bug. Email opt-out
          // stays — its condition (`showBillingAddress && !hideEmail`) is fully
          // static, so it never flips at runtime.
          ...(showBillingAddress && !hideEmail
            ? { fields: { billingDetails: { email: 'never' as const } } }
            : {}),
        }}
      />
      {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
      {showSubmitButton && (
        <Button
          type="submit"
          disabled={!stripe || submitting || isLoading}
          className="w-full cursor-pointer"
        >
          {submitting ? t('Processing...') : (submitText ?? t('Save Card'))}
        </Button>
      )}
    </form>
  );
}

/**
 * Stripe card/payment form wrapped in Elements provider.
 * Reusable across PaymentMethodPage (card update) and SubscriptionWizardDialog (checkout).
 *
 * Usage:
 *   1. Call stripeBuildElement query to get clientSecret
 *   2. Pass clientSecret to this component
 *   3. Handle onSuccess with the setupIntentId
 */
export function StripeCardForm(props: StripeCardFormProps) {
  const { stripePromise } = useStripeSetup();
  const {
    clientSecret,
    paymentMethodOrder,
    defaultEmail,
    emailInlineSlot,
    hideEmail,
    showBillingAddress,
    defaultBillingName,
    onBillingAddressChange,
    ...formProps
  } = props;

  const { appearance, isDark } = useStripeAppearance();

  if (!clientSecret) return null;

  return (
    <Elements
      key={`${clientSecret}-${isDark ? 'dark' : 'light'}`}
      stripe={stripePromise}
      options={{ clientSecret, appearance }}
    >
      <StripePaymentForm
        {...formProps}
        paymentMethodOrder={paymentMethodOrder}
        defaultEmail={defaultEmail}
        emailInlineSlot={emailInlineSlot}
        hideEmail={hideEmail}
        showBillingAddress={showBillingAddress}
        defaultBillingName={defaultBillingName}
        onBillingAddressChange={onBillingAddressChange}
      />
    </Elements>
  );
}
